if (!customElements.get('sticky-atc-bar')) {
  class StickyAtcBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.btn = this.querySelector('.sticky-atc-bar__btn');
      this.priceEl = this.querySelector('.js-sticky-atc-price');
      this.variantLabelEl = this.querySelector('.js-sticky-atc-variant');

      this.handleButtonClickBound = this.handleButtonClick.bind(this);
      this.btn?.addEventListener('click', this.handleButtonClickBound);

      this._resizeTimer = null;
      this.resizeHandler = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this.handleResize(), 200);
      };
      window.addEventListener('resize', this.resizeHandler);

      this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.setupObserver();
      this.setupDesktopScroll();
      this.setupVariantListener();
      requestAnimationFrame(() => {
        this.syncPriceFromDOM();
        this.syncDesktopScrollVisibility();
        this.syncMobileVisibility();
      });
    }

    /**
     * Móvil: mostrar cuando el botón principal queda bajo header + barra de anuncios (~180px).
     * Ocultar si el botón está visible o aún no se ha alcanzado al bajar.
     */
    getMobileStickyOffset() {
      return 180;
    }

    hasScrolledPastSubmitButton(rect) {
      return rect.bottom <= this.getMobileStickyOffset();
    }

    syncMobileVisibility() {
      if (!this.classList.contains('sticky-atc-bar--mobile-enabled')) return;
      if (window.innerWidth >= 750) return;

      const target = this.submitButton;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      if (this.hasScrolledPastSubmitButton(rect)) {
        this.show();
      } else {
        this.hide();
      }
    }

    setupObserver() {
      if (!this.classList.contains('sticky-atc-bar--mobile-enabled')) return;

      const target = this.submitButton;
      if (!target) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (window.innerWidth >= 750) return;
            this.syncMobileVisibility();
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(target);
    }

    setupDesktopScroll() {
      if (!this.classList.contains('sticky-atc-bar--desktop-enabled')) return;

      this.scrollHandlerDesktop = () => {
        if (window.innerWidth < 750) return;
        this.syncDesktopScrollVisibility();
      };
      window.addEventListener('scroll', this.scrollHandlerDesktop, { passive: true });
    }

    syncDesktopScrollVisibility() {
      if (!this.classList.contains('sticky-atc-bar--desktop-enabled')) return;
      if (window.innerWidth < 750) return;

      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > 0) {
        this.show();
      } else {
        this.hide();
      }
    }

    setupVariantListener() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== this.sectionId) return;
        requestAnimationFrame(() => this.syncFromVariant(data.variant));
      });
    }

    syncFromVariant(variant) {
      if (!variant) return;

      if (this.btn) {
        const btnText = this.btn.querySelector('.sticky-atc-bar__btn-text');
        if (variant.available) {
          this.btn.removeAttribute('disabled');
          if (btnText) btnText.textContent = this.btn.dataset.addText;
        } else {
          this.btn.setAttribute('disabled', '');
          if (btnText) btnText.textContent = this.btn.dataset.soldOutText;
        }
      }

      if (this.variantLabelEl && variant.title) {
        this.variantLabelEl.textContent = variant.title;
      }

      this.syncVariantImage(variant);
      this.syncPriceFromDOM();
    }

    updateStickyPrice({ compareText, currentText }) {
      this.priceEl.replaceChildren();

      const currentEl = document.createElement('span');
      currentEl.className = 'sticky-atc-bar__price-current';
      currentEl.textContent = currentText || '';
      this.priceEl.appendChild(currentEl);

      if (compareText) {
        const compareWrap = document.createElement('span');
        compareWrap.className = 'sticky-atc-bar__price-compare';
        const strike = document.createElement('s');
        strike.textContent = compareText;
        compareWrap.appendChild(strike);
        this.priceEl.appendChild(compareWrap);
      }
    }

    syncPriceFromDOM() {
      const priceSection = document.getElementById(`price-${this.sectionId}`);
      if (!priceSection || !this.priceEl) return;

      const salePriceContainer = priceSection.querySelector('.price__sale');
      const isOnSale =
        salePriceContainer &&
        !salePriceContainer.hidden &&
        !salePriceContainer.classList.contains('hidden') &&
        getComputedStyle(salePriceContainer).display !== 'none';

      const onMobile = window.innerWidth < 750;

      if (isOnSale) {
        const salePrice = salePriceContainer.querySelector('.price-item--sale');
        const comparePrice = salePriceContainer.querySelector('.price-item--regular');
        this.updateStickyPrice({
          compareText:
            !onMobile && comparePrice ? comparePrice.textContent.trim() : null,
          currentText: salePrice ? salePrice.textContent.trim() : '',
        });
      } else {
        const regularContainer = priceSection.querySelector('.price__regular');
        const regularPrice = regularContainer?.querySelector('.price-item--regular');
        if (regularPrice) {
          this.updateStickyPrice({ currentText: regularPrice.textContent.trim() });
        }
      }

      this.priceEl.classList.toggle('is-on-sale', isOnSale);
    }

    syncVariantImage(variant) {
      const img = this.querySelector('.js-sticky-atc-img');
      const wrap = this.querySelector('.js-sticky-atc-img-wrap');
      if (!img || !wrap) return;

      const featuredImage = variant?.featured_image;
      const featuredImageSrc = typeof featuredImage === 'string' ? featuredImage : featuredImage?.src;
      const rawSrc = variant?.featured_media?.preview_image?.src || featuredImageSrc;
      const fallbackSrc = wrap.dataset.defaultSrc;
      const fallbackSrcset = wrap.dataset.defaultSrcset;

      if (rawSrc) {
        const absolute = rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc;
        try {
          const url = new URL(absolute, window.location.origin);
          url.searchParams.set('width', '96');
          img.src = url.href;
        } catch {
          img.src = absolute;
        }
        img.removeAttribute('srcset');
        wrap.hidden = false;
      } else if (fallbackSrc) {
        img.src = fallbackSrc;
        if (fallbackSrcset) {
          img.setAttribute('srcset', fallbackSrcset);
        } else {
          img.removeAttribute('srcset');
        }
        wrap.hidden = false;
      } else {
        wrap.hidden = true;
      }
    }

    handleButtonClick() {
      const originalBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      originalBtn?.click();
    }

    show() {
      const isDesktopEnabled = this.classList.contains('sticky-atc-bar--desktop-enabled');
      const isMobileEnabled = this.classList.contains('sticky-atc-bar--mobile-enabled');
      const onMobile = window.innerWidth < 750;

      if ((onMobile && isMobileEnabled) || (!onMobile && isDesktopEnabled)) {
        this.classList.add('is-visible');
      }
    }

    hide() {
      this.classList.remove('is-visible');
    }

    handleResize() {
      const onMobile = window.innerWidth < 750;

      if (onMobile) {
        this.syncMobileVisibility();
      } else {
        this.syncDesktopScrollVisibility();
      }

      this.syncPriceFromDOM();

      const isDesktopEnabled = this.classList.contains('sticky-atc-bar--desktop-enabled');
      const isMobileEnabled = this.classList.contains('sticky-atc-bar--mobile-enabled');

      if ((onMobile && !isMobileEnabled) || (!onMobile && !isDesktopEnabled)) {
        this.hide();
      }
    }

    disconnectedCallback() {
      clearTimeout(this._resizeTimer);
      window.removeEventListener('resize', this.resizeHandler);
      if (this.scrollHandlerDesktop) {
        window.removeEventListener('scroll', this.scrollHandlerDesktop);
      }
      this.btn?.removeEventListener('click', this.handleButtonClickBound);
      this.observer?.disconnect();
      this.variantUnsubscribe?.();
    }
  }

  customElements.define('sticky-atc-bar', StickyAtcBar);
}
